<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>

      <BaseSelectDropdown
        :items="dropdownTypes"
        :selected-item="selectedItem"
        :disable="!modifiedData.isFromManager"
        :label="$t('material.materialType')"
        show-error-as-tooltip
        clearable
        :error="$v.type.required.$invalid ? $v.type.required.$message : ''"
        @update:selected="handleSelection"
        name="alterCodeType" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }"
        :label="$t('series.prefix')"
        name="prefix"
        v-model="modifiedData.prefixIncluded" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }"
        :label="$t('material.hasMixInfo')"
        name="hasMixInformation"
        v-model="modifiedData.hasMixInformation" />

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditAlterCodeType } from '@/services/api/materials';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { enumToDropdownItems } from '@/utils/enums';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { eAlterCodeType, type AlterCodeType } from '@/types/Materials';
import { type SelectDropdownItem } from '@/types/Base';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    alterCodeType: AlterCodeType;
  }>(),
  {}
);

const modifiedData = ref<AlterCodeType>(deepCopy<AlterCodeType>(props.alterCodeType));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.type = newData.alterCodeType || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.alterCodeType);
  },
  { deep: true }
);

const dropdownTypes = ref(enumToDropdownItems(eAlterCodeType));

const selectedItem = ref<SelectDropdownItem | null>(null);

function handleSelection(item: SelectDropdownItem) {
  selectedItem.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.eType = item ? +item.value : null;
    modifiedData.value.alterCodeType = item ? item.name : undefined;
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<AlterCodeType>(props.alterCodeType);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditAlterCodeType(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

onMounted(() => {
  if (modifiedData.value && modifiedData.value.eType !== undefined) {
    const selectedValue = dropdownTypes.value.find(
      (item) => item.value === modifiedData.value?.eType
    );

    if (selectedValue) {
      selectedItem.value = selectedValue;
    }
  }
});

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  type: modifiedData.value.alterCodeType || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  type: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
